// src/utils/dimensions.js
let dimensionsCache = new Map();

/**
 * Clears the dimensions cache.
 */
export const clearDimensionsCache = () => {
  dimensionsCache.clear();
  console.log("[util.dimensions]:setDimensions: Cache cleared");
};

/**
 * Calculates dimensions (widthInches, heightInches) for a Fabric.js i-text object.
 * @param {Object} obj - Fabric.js i-text object with text, fontFamily, size, etc.
 * @param {string} fontCode - Font code (e.g., "Helvetica").
 * @param {string} sizeCode - Size code (e.g., "02", "06").
 * @param {Array} fonts - Array of font objects from constants/fonts.js.
 * @param {Object} [options] - Options including scaleX, precision, productCode, heightOverride.
 * @returns {Object} Calculated dimensions and metadata.
 */
export const setDimensions = (obj, fontCode, sizeCode, fonts, options = {}) => {
  try {
    // Log start with text object properties
    console.log("[util.dimensions]:setDimensions: Starting", {
      text: obj?.text,
      fontCode,
      sizeCode,
      fontsLength: fonts?.length || 0,
      options,
      textObjProps: obj
        ? {
            text: obj.text,
            fontFamily: obj.fontFamily,
            selectable: obj.selectable,
            fontSize: obj.fontSize,
            scaleX: obj.scaleX,
            size: obj.size,
          }
        : null,
    });

    // Validate inputs
    if (!obj || !obj.text || !fontCode || !sizeCode || !Array.isArray(fonts)) {
      console.warn("[util.dimensions]:setDimensions: Invalid inputs", {
        text: obj?.text,
        fontCode,
        sizeCode,
        fontsAvailable: !!fonts,
        fontsLength: fonts?.length,
      });
      return { widthInches: 0, heightInches: 0 };
    }

    const text = obj.text.trim();
    if (!text) {
      console.warn("[util.dimensions]:setDimensions: Empty text", {
        fontCode,
        sizeCode,
      });
      return { widthInches: 0, heightInches: 0 };
    }

    // Find font
    const font = fonts.find((f) => f?.code === fontCode) || {
      code: fontCode,
      name: fontCode,
      sizes: [],
      fontAdjustment: 1.0,
      charWidths: {},
    };
    console.log("[util.dimensions]:setDimensions: Font lookup", {
      fontCode,
      fontName: font.name,
      fontCodeFound: font.code,
      sizesLength: font.sizes?.length || 0,
      sizesAvailable:
        font.sizes?.map((s) => ({
          code: s.code,
          height: s.height,
        })) || [],
    });

    if (!font.code || font.code === fontCode) {
      console.warn("[util.dimensions]:setDimensions: No valid font found", {
        fontCode,
        fontsLength: fonts.length,
        availableFonts: fonts.map((f) => f.code),
      });
    }

    // Size validation with expected heights
    const expectedHeights = {
      "02": 2.0,
      "03": 3.0,
      "04": 4.0,
      "06": 6.0,
      10: 10.0,
    };
    const sizeObj = font.sizes?.find((s) => s?.code === sizeCode) ||
      font.sizes?.[0] || {
        code: "Unknown",
        height: expectedHeights[sizeCode] || parseFloat(sizeCode) || 2.0,
        name: "Unknown",
      };
    let heightInches =
      options.heightOverride ||
      sizeObj.height ||
      expectedHeights[sizeCode] ||
      parseFloat(sizeCode) ||
      2.0;

    console.log("[util.dimensions]:setDimensions: Size validation", {
      sizeCode,
      fontCode,
      sizeObj: {
        code: sizeObj.code,
        height: sizeObj.height,
        name: sizeObj.name,
      },
      heightInches,
      sizesAvailable:
        font.sizes?.map((s) => ({
          code: s.code,
          height: s.height,
        })) || [],
    });

    // Warn if size is invalid
    if (!font.sizes?.some((s) => s?.code === sizeCode)) {
      console.warn("[util.dimensions]:setDimensions: Invalid size for code", {
        sizeCode,
        fontCode,
        sizeObj,
        sizesAvailable: font.sizes?.map((s) => s.code) || [],
      });
    }

    // Check expected height
    if (
      expectedHeights[sizeCode] &&
      heightInches !== expectedHeights[sizeCode]
    ) {
      console.warn(
        "[util.dimensions]:setDimensions: Unexpected height for sizeCode",
        {
          sizeCode,
          expectedHeight: expectedHeights[sizeCode],
          actualHeight: heightInches,
          sizeObj,
        }
      );
    }

    // Normalize scale
    const scaleX = Math.min(options.scaleX || 1, 1); // Cap at 1, as in dimensions2.js
    console.log("[util.dimensions]:setDimensions: Scale normalization", {
      originalScaleX: options.scaleX,
      normalizedScaleX: scaleX,
    });

    // Calculate dimensions
    const fontAdjustment = font.fontAdjustment || 1.0;
    let widthInches = 0;
    const precision = options.precision || "precise";

    if (precision === "precise" && font.charWidths) {
      let totalWidthRatio = 0;
      for (const char of text) {
        totalWidthRatio += font.charWidths[char] || 1.0; // Fallback to 1.0
      }
      widthInches = totalWidthRatio * heightInches * fontAdjustment * scaleX;
    } else {
      const avgCharWidth = 0.6; // From dimensions2.js simplified mode
      const heightScale = heightInches / 2; // Normalize to 2-inch base
      widthInches =
        text.length *
        avgCharWidth *
        heightInches *
        scaleX *
        fontAdjustment *
        heightScale;
    }

    const calculated = {
      productCode: options.productCode || "",
      text,
      fontCode,
      sizeCode,
      widthInches,
      heightInches,
      precision,
      fontAdjustment,
      scaleX,
      charWidthsAvailable: !!font.charWidths,
      cacheHit: dimensionsCache.has(
        JSON.stringify({ text, fontCode, sizeCode, scaleX, precision })
      ),
    };

    // Cache result
    const cacheKey = JSON.stringify({
      text,
      fontCode,
      sizeCode,
      scaleX,
      precision,
    });
    dimensionsCache.set(cacheKey, calculated);

    console.log("[util.dimensions]:setDimensions: Calculated", calculated);

    return calculated;
  } catch (error) {
    console.error("[util.dimensions]:setDimensions: Error", {
      error: error.message,
      text: obj?.text,
      fontCode,
      sizeCode,
      fontsLength: fonts?.length,
      textObjProps: obj
        ? {
            fontFamily: obj.fontFamily,
            selectable: obj.selectable,
            fontSize: obj.fontSize,
            scaleX: obj.scaleX,
            size: obj.size,
          }
        : null,
    });
    return { widthInches: 0, heightInches: 0 };
  }
};
